import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { motion, AnimatePresence } from "framer-motion";
import "mapbox-gl/dist/mapbox-gl.css";
import { registrarTapConDistancia } from "../api";
import { calcularDistancia } from "../utils/geo";

const NavigationMapModal = ({
  comercioLat,
  comercioLng,
  comercioLogo,
  comercioNombre,
  horarioHoy,
  onClose,
  tipoHorario,
  generoUsuario,
  id_sucursal
}) => {
  const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [distance, setDistance] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [duration, setDuration] = useState(null);

  const formatearHora12 = (hora) => {
    const [h, m] = hora.split(":");
    const date = new Date();
    date.setHours(parseInt(h), parseInt(m));
    return date.toLocaleTimeString("es-GT", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatearDuracion = (minutos) => {
    const h = Math.floor(minutos / 60);
    const m = Math.round(minutos % 60);
    if (h > 0) return `${h} h ${m} min`;
    return `${m} min`;
  };

  const userLat = parseFloat(localStorage.getItem("userLat"));
  const userLng = parseFloat(localStorage.getItem("userLng"));

  useEffect(() => {
    if (!userLat || !userLng || !comercioLat || !comercioLng) return;
    if (!mapReady) return;

    if (mapRef.current) {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }

      mapboxgl.accessToken = accessToken;

      const markerWrapper = document.createElement("div");
      markerWrapper.style.position = "relative";
      markerWrapper.style.width = "40px";
      markerWrapper.style.height = "40px";

      const map = new mapboxgl.Map({
        container: mapRef.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [(userLng + comercioLng) / 2, (userLat + comercioLat) / 2],
        zoom: 15,
        pitch: 60,
        bearing: -20,
        antialias: true
      });

      mapInstance.current = map;

      const pulse = document.createElement("div");
      pulse.className = "mapbox-user-ping";

      const userEl = document.createElement("div");
      userEl.className = "mapbox-user-marker";
      userEl.style.backgroundImage = `url('${
        generoUsuario === "masculino"
          ? "/assets/images/masculino.png"
          : generoUsuario === "femenino"
          ? "/assets/images/femenino.png"
          : "/assets/images/indefinido.png"
      }')`;

      userEl.style.width = "40px";
      userEl.style.height = "40px";
      userEl.style.backgroundSize = "cover";
      userEl.style.borderRadius = "50%";
      userEl.style.border = "2px solid white";

      markerWrapper.appendChild(pulse);
      markerWrapper.appendChild(userEl);

      new mapboxgl.Marker(markerWrapper)
        .setLngLat([userLng, userLat])
        .setPopup(new mapboxgl.Popup().setText("Tu ubicación"))
        .addTo(map);

      // Comercio
      const comercioEl = document.createElement("div");
      comercioEl.style.backgroundImage = `url('${comercioLogo}')`;
      comercioEl.style.width = "45px";
      comercioEl.style.height = "45px";
      comercioEl.style.backgroundSize = "cover";
      comercioEl.style.borderRadius = "50%";
      comercioEl.style.border = "2px solid white";

      new mapboxgl.Marker(comercioEl)
        .setLngLat([comercioLng, comercioLat])
        .setPopup(new mapboxgl.Popup().setText(comercioNombre))
        .addTo(map);

      map.on("load", async () => {
        try {
          const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${userLng},${userLat};${comercioLng},${comercioLat}?geometries=geojson&overview=full&access_token=${accessToken}`;
          const response = await fetch(url);
          const data = await response.json();

          if (data.routes && data.routes.length > 0) {
            const route = data.routes[0];

            map.addSource("route", {
              type: "geojson",
              data: {
                type: "Feature",
                geometry: route.geometry,
              },
            });

            map.addLayer({
              id: "route-line",
              type: "line",
              source: "route",
              layout: {
                "line-cap": "round",
                "line-join": "round",
              },
              paint: {
                "line-color": "#00bfff",
                "line-width": 6,
                "line-opacity": 0.7,
              },
            });

            const bounds = route.geometry.coordinates.reduce(
              (b, coord) => b.extend(coord),
              new mapboxgl.LngLatBounds(
                route.geometry.coordinates[0],
                route.geometry.coordinates[0]
              )
            );

            map.fitBounds(bounds, {
              padding: 30,
              pitch: 60,
              bearing: -20,
            });

            setDistance((route.distance / 1000).toFixed(2));
            setDuration(Math.ceil(route.duration / 60));
          }
        } catch (err) {
          console.error("Error al obtener la ruta:", err);
        }
      });
    }

    return () => {
      try {
        if (mapInstance.current) {
          mapInstance.current.remove();
          mapInstance.current = null;
        }
      } catch (e) {
        console.warn("Error limpiando mapa:", e.message);
      }
    };
  }, [comercioLat, comercioLng, mapReady]);

  const distancia = userLat && userLng && comercioLat && comercioLng
    ? calcularDistancia(userLat, userLng, comercioLat, comercioLng)
    : null;

  const horarioTexto = tipoHorario === 1
    ? "Siempre abierto"
    : horarioHoy
      ? `${formatearHora12(horarioHoy.inicio)} - ${formatearHora12(horarioHoy.fin)}`
      : "Horario no disponible";

  return (
    <AnimatePresence>
      {true && (
        <>
          <motion.div
            key="navigation-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overlay"
            style={{
              position: "fixed",
              inset: 0,
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.4)",
              zIndex: 9998
            }}
            onClick={onClose}
          />

          <motion.div
            key="navigation-map"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="navigation-modal"
            style={{
              position: "fixed",
              bottom: 0,
              height: "100%",
              left: 0,
              right: 0,
              top: 0,
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
            }}
            onAnimationComplete={() => setMapReady(true)}
          >
            <div ref={mapRef} className="navigation-map-full" />

            <div className="route-controls">
              <button className="navigation-close-btn" onClick={onClose}>×</button>
              <div className="custom-zoom-controls">
                <button onClick={() => mapInstance.current.zoomIn()}>+</button>
                <button onClick={() => mapInstance.current.zoomOut()}>−</button>
              </div>
            </div>

            <div className="navigation-card">
              <div className="navigation-header">
                <h3>Detalles del destino</h3>
              </div>
              <div className="navigation-body">
                <div className="delivery-info">
                  <div>
                    <p className="label">Tiempo de llegada: (Aproximado)</p>
                    <p className="value">{duration ? formatearDuracion(duration) : "Calculando..."}</p>
                  </div>
                  <div>
                    <p className="label">Distancia:</p>
                    <p className="value">{distancia ? `${distancia}` : "Calculando..."}</p>
                  </div>
                </div>
                <div className="tracking-pill-inner">
                  <div className="tracking-info">
                    <img src={comercioLogo} alt={comercioNombre} className="tracking-avatar" />
                    <div>
                      <div className="tracking-name">{comercioNombre}</div>
                      <div className="tracking-role">{horarioTexto}</div>
                    </div>
                  </div>
                  <div className="tracking-actions">
                    <button className="pill-btn"
                      onClick={() => {
                        const id_perfil = localStorage.getItem("id_perfil");
                        registrarTapConDistancia(id_sucursal, "waze", distancia, id_perfil);
                      
                        const url = `https://waze.com/ul?ll=${comercioLat},${comercioLng}&navigate=yes`;
                      
                        if (window.flutter_inappwebview) {
                          window.flutter_inappwebview.callHandler("abrirEnlaceExterno", url);
                        } else {
                          window.open(url, "_blank");
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M20.54 6.63c.69.94 1.15 2.04 1.35 3.19c.21 1.25.11 2.52-.31 3.72a7.35 7.35 0 0 1-2 3.06a9.1 9.1 0 0 1-2.26 1.58c.41 1.07-.13 2.27-1.2 2.68c-.24.09-.49.14-.74.14a2.08 2.08 0 0 1-2.07-2h-3.07c-.11 1.14-1.13 2-2.27 1.87c-1.06-.1-1.86-.98-1.88-2.04c.01-.19.04-.39.1-.57a8.37 8.37 0 0 1-4-2.85c-.33-.44-.23-1.07.23-1.41c.18-.14.4-.22.63-.22c.72 0 1-.25 1.17-.63c.24-.72.38-1.47.39-2.23c.03-.53.09-1.05.17-1.57A7.3 7.3 0 0 1 7.5 5c1.66-1.3 3.69-2 5.79-2c1.43 0 2.84.35 4.11 1a8.7 8.7 0 0 1 3.14 2.63m-3.82 10.68c1.78-.81 3.18-2.27 3.87-4.1c1.62-4.94-2.59-9.16-7.3-9.16c-.35 0-.71.02-1.06.07C9.36 4.5 6.4 6.5 5.81 9.5c-.38 2 .19 5.29-2.76 5.29C4 16 5.32 16.93 6.81 17.37c.85-.76 2.16-.68 2.93.18c.11.12.2.25.26.39h3.55c.52-1.02 1.78-1.44 2.8-.9c.15.08.25.17.37.27m-5.75-7c-.58.03-1.09-.41-1.12-1c-.03-.58.42-1.08 1-1.12c.58-.03 1.09.42 1.12 1.06a1 1 0 0 1-.97 1.04zm4.69 0c-.58.03-1.09-.41-1.12-1c-.04-.58.42-1.08 1-1.12c.58-.03 1.09.42 1.12 1.06c.02.55-.41 1.02-1 1.04zm-5.95 1.76c-.06-.28.13-.57.41-.62s.56.13.62.41a2.5 2.5 0 0 0 2.58 1.74c1.14.06 2.18-.64 2.57-1.72c.14-.26.46-.38.71-.23c.18.1.29.27.29.47c-.19.71-.63 1.33-1.23 1.76c-.69.48-1.5.75-2.34.76h-.11c-1.63.07-3.1-1-3.53-2.58z" strokeWidth="0.1" stroke="currentColor" />
                    </svg>
                    </button>
                    <button className="pill-btn"
                      onClick={() => {
                        const id_perfil = localStorage.getItem("id_perfil");
                        registrarTapConDistancia(id_sucursal, "maps", distancia, id_perfil);
                        const url = `https://www.google.com/maps/dir/?api=1&destination=${comercioLat},${comercioLng}`;
                        if (window.flutter_inappwebview) {
                          window.flutter_inappwebview.callHandler("abrirEnlaceExterno", url);
                        } else {
                          window.open(url, "_blank");
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">
                        <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" color="currentColor">
                            <circle cx="12" cy="9" r="2.5" />
                            <path d="m9 17l9-11M6 12l8-9.5M7 5l3 2.5M12.01 22c-.354 0-.519-.151-.701-.659c-.48-1.69-1.378-3.15-2.467-4.521c-.986-1.317-2.44-2.816-3.196-4.524C3.546 7.655 6.801 1.992 11.993 2c5.335-.015 8.543 5.851 6.263 10.545a21 21 0 0 1-2.339 3.333c-1.381 1.631-2.622 3.397-3.211 5.466c-.136.399-.31.656-.696.656" />
                        </g>
                    </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NavigationMapModal;
